import React, { useState, useMemo, useEffect } from 'react';
import apiService from './apiService';
import PhoneVerifyModal from './PhoneVerifyModal';
import BotShield, { botFields, resetBotShield } from './BotShield';
import { COUNTRIES, DEFAULT_COUNTRY, countryByIso, flagOf, buildPhone } from './countries';
import { LEGAL_VERSION } from './LegalGate';

const group3 = (d) => String(d || '').replace(/(\d{3})(?=\d)/g, '$1 ');
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());

/**
 * Connexion sans mot de passe « numéro d'abord » sur les sites web (2026-10-30), même parcours que
 * les applications : indicatif + numéro → code WhatsApp (PhoneVerifyModal, e-mail en secours) →
 * compte connu = session ; numéro nouveau = nom (+ e-mail facultatif, + ville pour le client) → session.
 * La session dure 180 jours sur ce navigateur (jeton d'appareil, voir apiService.silentRefresh).
 *
 * Props : app ('client' | 'driver'), accent (couleur), brand (élément affiché au-dessus du titre),
 *         onSession(resp) → Promise — reçoit la réponse de /auth/otp-login ; l'appelant enregistre et navigue
 */
export default function PhoneLoginWeb({ app = 'client', accent = '#F54119', brand = null, onSession }) {
  const [country, setCountry] = useState(countryByIso(DEFAULT_COUNTRY));
  const [digits, setDigits] = useState('');
  const [otpPhone, setOtpPhone] = useState(null);
  const [step, setStep] = useState('phone'); // 'phone' | 'profile'
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cities, setCities] = useState([]);
  const [citySlug, setCitySlug] = useState('');
  const [cityDetecting, setCityDetecting] = useState(false);
  const [cityDetected, setCityDetected] = useState(false);

  const phone = useMemo(() => buildPhone(country, digits), [country, digits]);
  const digitsOk = digits.length >= Math.min(7, country.max || 7);

  const onChangeDigits = (e) => {
    let d = e.target.value.replace(/[^0-9]/g, '');
    if (d.startsWith(country.dial) && d.length > country.max) d = d.slice(country.dial.length);
    setDigits(d.replace(/^0+/, '').slice(0, country.max));
    setErr('');
  };

  const otpLogin = async (tok, extra = {}) => {
    const r = await apiService.post('/auth/otp-login', {
      phone, verification_token: tok, app, device: apiService.deviceInfo(), language: 'fr', terms_version: LEGAL_VERSION, ...extra,
    });
    if (!r?.success) throw r;
    return r;
  };

  // Ville du nouveau compte client : position du navigateur → /cities/detect, sinon liste
  const loadCities = async () => {
    try {
      const r = await apiService.get('/cities');
      const list = Array.isArray(r) ? r : (r?.cities || []);
      setCities(list);
      return list;
    } catch (_) { return []; }
  };
  const detectCity = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await apiService.get(`/cities/detect?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          resolve(r?.success && r.city ? r.city : null);
        } catch (_) { resolve(null); }
      },
      () => resolve(null),
      { timeout: 8000, maximumAge: 300000 },
    );
  });

  const onVerified = async (tok) => {
    setOtpPhone(null); setToken(tok); setBusy(true); setErr('');
    try {
      const r = await otpLogin(tok);
      if (r.needs_profile) {
        setStep('profile');
        if (app === 'client') {
          setCityDetecting(true);
          const [detected] = await Promise.all([detectCity(), loadCities()]);
          if (detected?.slug) { setCitySlug(detected.slug); setCityDetected(true); }
          setCityDetecting(false);
        }
        return;
      }
      await onSession(r);
    } catch (e) {
      const c = e?.code || e?.data?.code;
      setErr(c === 'phone_verification_invalid' ? 'La vérification a expiré. Recommencez.' : (e?.error || e?.message || 'Connexion impossible. Réessayez.'));
      if (c === 'phone_verification_invalid') setStep('phone');
    } finally {
      setBusy(false);
    }
  };

  const submitProfile = async (e) => {
    e.preventDefault(); setErr('');
    if (!name.trim()) { setErr('Indiquez votre nom.'); return; }
    if (email.trim() && !isEmail(email)) { setErr('Adresse e-mail invalide.'); return; }
    if (app === 'client' && !citySlug) { setErr('Choisissez votre ville.'); return; }
    setBusy(true);
    try {
      const r = await otpLogin(token, { name: name.trim(), ...(email.trim() ? { email: email.trim() } : {}), ...(citySlug ? { city_slug: citySlug } : {}) });
      await onSession(r);
    } catch (e2) {
      const c = e2?.code || e2?.data?.code;
      if (c === 'phone_verification_invalid') { setErr('La vérification a expiré. Recommencez.'); setStep('phone'); }
      else setErr(e2?.error || e2?.message || 'Connexion impossible. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (step === 'profile' && app === 'client' && !cities.length && !cityDetecting) loadCities(); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Anti-robot (Turnstile + pièges) : exigé par le serveur pour l'envoi du code depuis un navigateur.
  // Le jeton Turnstile est à usage unique → lu puis widget réarmé à chaque envoi (renvoi du code compris).
  const botNow = () => { const f = botFields('phone-login'); setTimeout(() => resetBotShield('phone-login'), 0); return f; };

  const field = { display: 'flex', alignItems: 'center', gap: 8, height: 56, border: '1.5px solid #DCE0E6', borderRadius: 14, background: '#F4F5F7', padding: '0 8px', boxSizing: 'border-box' };
  const input = { width: '100%', height: 54, border: '1.5px solid #DCE0E6', borderRadius: 14, background: '#F4F5F7', padding: '0 14px', fontSize: 16, color: '#1C1C1C', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const btn = (disabled) => ({ width: '100%', height: 52, borderRadius: 14, border: 'none', background: disabled ? `${accent}22` : accent, color: disabled ? accent : '#fff', fontWeight: 800, fontSize: 16, cursor: disabled ? 'default' : 'pointer', marginTop: 18, fontFamily: 'inherit', transition: 'transform 120ms ease' });
  const h2 = { margin: '6px 0 6px', fontSize: 26, fontWeight: 800, color: '#1C1C1C', letterSpacing: '-0.01em', textAlign: 'left' };
  const sub = { margin: '0 0 18px', color: '#5C636E', fontSize: 14, lineHeight: 1.45, textAlign: 'left' };
  const errBox = err ? <p style={{ color: '#B91C1C', fontSize: 14, fontWeight: 600, margin: '10px 0 0' }}>{err}</p> : null;

  if (step === 'profile') {
    return (
      <form onSubmit={submitProfile} style={{ textAlign: 'left' }}>
        {brand}
        <h2 style={h2}>{app === 'driver' ? 'Bienvenue' : 'Bienvenue sur Mbiyo'}</h2>
        <p style={sub}>Première visite avec le <b style={{ color: '#1C1C1C' }}>+{country.dial} {group3(digits)}</b>. {app === 'driver' ? 'Votre nom, et on continue.' : "Deux informations et c'est parti."}</p>
        <div style={{ display: 'grid', gap: 10 }}>
          <input style={input} value={name} onChange={(e) => { setName(e.target.value); setErr(''); }} placeholder="Prénom et nom" autoFocus autoComplete="name" />
          <input style={input} value={email} onChange={(e) => { setEmail(e.target.value); setErr(''); }} placeholder="E-mail (facultatif)" type="email" autoComplete="email" />
          {app === 'client' ? (
            <div>
              <select style={{ ...input, appearance: 'auto' }} value={citySlug} onChange={(e) => { setCitySlug(e.target.value); setCityDetected(false); setErr(''); }} disabled={cityDetecting}>
                <option value="">{cityDetecting ? 'Recherche de votre ville…' : 'Choisir ma ville'}</option>
                {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              {cityDetected ? <small style={{ color: '#5C636E', display: 'block', marginTop: 4 }}>Détectée par votre position · modifiable</small> : null}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: '#5C636E', background: '#F4F5F7', border: '1px solid #DCE0E6', borderRadius: 12, padding: '10px 12px' }}>Ensuite : votre photo de profil, vos documents et votre véhicule. Comptez 5 minutes.</p>
          )}
        </div>
        {errBox}
        <button type="submit" disabled={busy || !name.trim()} style={btn(!name.trim())}>{busy ? 'Un instant…' : app === 'driver' ? 'Devenir chauffeur' : 'Commencer'}</button>
      </form>
    );
  }

  return (
    <div style={{ textAlign: 'left' }}>
      {brand}
      <h2 style={h2}>Entrez votre numéro</h2>
      <p style={sub}>Vous recevrez un code sur WhatsApp.</p>
      <form onSubmit={(e) => { e.preventDefault(); if (digitsOk && !busy) { setErr(''); setOtpPhone(phone); } }}>
        <div style={field}>
          <select aria-label="Pays" value={country.iso} onChange={(e) => { const c = countryByIso(e.target.value); setCountry(c); setDigits((d) => d.slice(0, c.max)); }}
            style={{ height: 38, borderRadius: 10, border: '1px solid #DCE0E6', background: '#fff', fontSize: 15, fontWeight: 700, color: '#1C1C1C', padding: '0 6px', fontFamily: 'inherit', maxWidth: 150 }}>
            {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{flagOf(c.iso)} +{c.dial} {c.name}</option>)}
          </select>
          <input
            type="tel" inputMode="numeric" autoComplete="tel-national" autoFocus
            value={group3(digits)} onChange={onChangeDigits}
            placeholder={country.iso === 'CD' ? '812 345 678' : 'Numéro'}
            aria-label="Numéro de téléphone"
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 20, fontWeight: 600, color: '#1C1C1C', letterSpacing: '0.02em', outline: 'none', fontFamily: 'inherit' }}
          />
          {digits ? <button type="button" onClick={() => setDigits('')} aria-label="Effacer" style={{ width: 26, height: 26, borderRadius: 13, border: 'none', background: '#DCE0E6', color: '#5C636E', cursor: 'pointer', fontSize: 12 }}>✕</button> : null}
        </div>
        <div style={{ minHeight: 22, marginTop: 10, fontSize: 13, color: '#5C636E' }}>
          {digitsOk && !err ? <span>💬 Le code arrivera sur WhatsApp au +{country.dial} {group3(digits)}</span> : null}
        </div>
        {errBox}
        <BotShield formKey="phone-login" baseURL={apiService.baseURL} />
        <button type="submit" disabled={busy || !digitsOk} style={btn(!digitsOk)}>{busy ? 'Connexion…' : 'Recevoir le code'}</button>
      </form>
      <p style={{ fontSize: 11.5, color: '#9AA0A8', textAlign: 'center', marginTop: 14, lineHeight: 1.4 }}>
        En continuant, vous acceptez les <a href="https://mbiyo.app/cgu" target="_blank" rel="noreferrer" style={{ color: '#9AA0A8' }}>conditions d'utilisation de Mbiyo</a>.
      </p>
      <PhoneVerifyModal open={!!otpPhone} phone={otpPhone} purpose="login" bot={botNow} onVerified={onVerified} onClose={() => setOtpPhone(null)} />
    </div>
  );
}
